import React, {useState} from "react"
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import {useQuery, useMutation} from "@tanstack/react-query"

import {CustomerCreatedPage} from "./CustomerCreatedPage"

import {useAgent} from "@indebted/hooks/Agent"
import {Layout} from "@indebted/components/Layout"
import {useNotification} from "@indebted/components/Notification"
import {api} from "@indebted/api"
import type {DevToolsCreateCustomerResponse} from "@indebted/api"
import {colors} from "@indebted/theme/colors"
import {Text} from "@indebted/theme/Text"

type AccountInput = {
	balance: number
	statementOfWorkID: string
}

type CustomerInput = {
	fullName: string
	dob: string
	email: string
	phone: string
}

const defaultCustomer: CustomerInput = {
	fullName: "Test Customer",
	dob: "1990-01-01",
	email: "test+customer@example.com",
	phone: "+61400000000",
}

const defaultAccount = (): AccountInput => ({
	balance: 10000,
	statementOfWorkID: "",
})

const cardSx = {
	background: colors.primary.white,
	borderRadius: "12px",
	boxShadow: "0px 4px 14px 2px rgba(54,59,100,0.10)",
	width: "100%",
	maxWidth: 600,
	p: 4,
	height: "fit-content",
} as const

function CreateCustomerPage() {
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [result, setResult] = useState<DevToolsCreateCustomerResponse | null>(null)

	const {
		data: clients = [],
		isLoading: clientsLoading,
		isError: clientsError,
	} = useQuery({
		queryKey: ["devTools", "clients"],
		queryFn: () => api.devTools.listClients(),
	})
	const [selectedCountry, setSelectedCountry] = useState<string>("")
	const availableCountries = [...new Set(clients.map((c) => c.country).filter(Boolean))].sort()
	const filteredClients = selectedCountry ? clients.filter((c) => c.country === selectedCountry) : clients

	const [selectedClientID, setSelectedClientID] = useState<string | undefined>(undefined)
	const effectiveClientID = selectedClientID ?? filteredClients[0]?.id ?? ""
	const selectedClient = clients.find((c) => c.id === effectiveClientID)
	const [customer, setCustomer] = useState<CustomerInput>({
		...defaultCustomer,
		email: agentEmail ?? defaultCustomer.email,
	})
	const [accounts, setAccounts] = useState<AccountInput[]>([defaultAccount()])
	const [expediteProcesses, setExpediteProcesses] = useState(true)
	const [simulateScrubs, setSimulateScrubs] = useState(true)

	const effectiveAccounts = accounts.map((a) => ({
		...a,
		statementOfWorkID: a.statementOfWorkID || selectedClient?.statementsOfWork[0]?.id || "",
	}))

	const {mutate: createCustomer, isPending: submitting} = useMutation({
		mutationFn: () =>
			api.devTools.createCustomer({
				clientID: effectiveClientID,
				customer,
				accounts: effectiveAccounts,
				expediteProcesses,
				simulateScrubs,
			}),
		onSuccess: (data) => setResult(data),
		onError: () => notification.error("Failed to create customer"),
	})

	React.useEffect(() => {
		if (clientsError) {
			notification.error("Failed to load clients")
		}
	}, [clientsError, notification])

	const updateAccount = (index: number, patch: Partial<AccountInput>) => {
		setAccounts((prev) => prev.map((a, i) => (i === index ? {...a, ...patch} : a)))
	}

	const removeAccount = (index: number) => {
		setAccounts((prev) => prev.filter((_, i) => i !== index))
	}

	const reset = () => setResult(null)

	if (result) {
		return <CustomerCreatedPage result={result} onReset={reset} />
	}

	return (
		<Layout>
			<Box
				sx={{
					background: colors.gray["100"],
					minHeight: "100%",
					display: "flex",
					justifyContent: "center",
					py: 4,
					px: 2,
				}}
			>
				<Box sx={cardSx}>
					<Text.Large.SemiBold
						color={colors.gray["900"]}
						sx={{
							pb: 2.5,
							borderBottom: `1px solid ${colors.gray["200"]}`,
							mb: 3,
						}}
					>
						Dev Tools — Create Test Customer
					</Text.Large.SemiBold>

					{clientsLoading ? (
						<Box display="flex" justifyContent="center" py={4}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<Box display="flex" flexDirection="column" gap={2} mb={3}>
							<FormControl fullWidth>
								<InputLabel shrink>Country</InputLabel>
								<Select
									label="Country"
									value={selectedCountry}
									displayEmpty
									notched
									renderValue={(value) => (value === "" ? "All countries" : value)}
									onChange={(e) => {
										setSelectedCountry(e.target.value)
										setSelectedClientID(undefined)
										setAccounts((prev) => prev.map((a) => ({...a, statementOfWorkID: ""})))
									}}
									sx={{borderRadius: "8px"}}
								>
									<MenuItem value="">All countries</MenuItem>
									{availableCountries.map((country) => (
										<MenuItem key={country} value={country}>
											{country}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl fullWidth>
								<InputLabel>Client</InputLabel>
								<Select
									label="Client"
									value={effectiveClientID}
									onChange={(e) => {
										setSelectedClientID(e.target.value)
										setAccounts((prev) => prev.map((a) => ({...a, statementOfWorkID: ""})))
									}}
									sx={{borderRadius: "8px"}}
								>
									{filteredClients.map((c) => (
										<MenuItem key={c.id} value={c.id}>
											{c.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					)}

					<Box mb={2}>
						<Text.ExtraSmall.Bold
							color={colors.gray["500"]}
							sx={{textTransform: "uppercase", letterSpacing: "0.07em"}}
						>
							Customer
						</Text.ExtraSmall.Bold>
					</Box>
					<Box display="flex" flexDirection="column" gap={2} mb={3}>
						<TextField
							label="Full Name"
							value={customer.fullName}
							onChange={(e) => setCustomer((prev) => ({...prev, fullName: e.target.value}))}
							slotProps={{input: {sx: {borderRadius: "8px"}}}}
						/>
						<TextField
							label="Date of Birth"
							placeholder="YYYY-MM-DD"
							value={customer.dob}
							onChange={(e) => setCustomer((prev) => ({...prev, dob: e.target.value}))}
							slotProps={{input: {sx: {borderRadius: "8px"}}}}
						/>
						<TextField
							label="Email"
							value={customer.email}
							onChange={(e) => setCustomer((prev) => ({...prev, email: e.target.value}))}
							slotProps={{input: {sx: {borderRadius: "8px"}}}}
						/>
						<TextField
							label="Phone"
							value={customer.phone}
							onChange={(e) => setCustomer((prev) => ({...prev, phone: e.target.value}))}
							slotProps={{input: {sx: {borderRadius: "8px"}}}}
						/>
					</Box>

					<Divider sx={{mb: 3}} />

					<Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
						<Text.ExtraSmall.Bold
							color={colors.gray["500"]}
							sx={{textTransform: "uppercase", letterSpacing: "0.07em"}}
						>
							Accounts
						</Text.ExtraSmall.Bold>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setAccounts((prev) => [...prev, defaultAccount()])}
							sx={[
								Text.ExtraSmall.Bold.css,
								{
									borderColor: colors.warmBlue["900"],
									color: colors.warmBlue["900"],
									borderRadius: "8px",
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									px: 1.5,
									py: 0.75,
									"&:hover": {background: colors.warmBlue["25"]},
								},
							]}
						>
							+ Add Account
						</Button>
					</Box>

					{accounts.map((account, index) => (
						<Paper
							key={index}
							variant="outlined"
							sx={{
								borderColor: colors.gray["200"],
								borderRadius: "8px",
								p: 2,
								mb: 2,
							}}
						>
							<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
								<Text.ExtraSmall.Bold color={colors.gray["600"]}>
									Account {index + 1}
								</Text.ExtraSmall.Bold>
								{accounts.length > 1 && (
									<IconButton size="small" onClick={() => removeAccount(index)}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								)}
							</Box>
							<Box display="flex" flexDirection="column" gap={2}>
								<TextField
									label="Balance (cents)"
									type="number"
									value={account.balance}
									onChange={(e) => updateAccount(index, {balance: parseInt(e.target.value, 10) || 0})}
									slotProps={{input: {sx: {borderRadius: "8px"}}}}
								/>
								{selectedClient && selectedClient.statementsOfWork.length > 0 && (
									<FormControl fullWidth>
										<InputLabel>Statement of Work</InputLabel>
										<Select
											label="Statement of Work"
											value={
												account.statementOfWorkID ||
												selectedClient.statementsOfWork[0]?.id ||
												""
											}
											onChange={(e) => updateAccount(index, {statementOfWorkID: e.target.value})}
											sx={{borderRadius: "8px"}}
										>
											{selectedClient.statementsOfWork.map((sow) => (
												<MenuItem key={sow.id} value={sow.id}>
													{sow.payoutType} · {sow.productType} ({sow.id.slice(0, 8)}…)
												</MenuItem>
											))}
										</Select>
									</FormControl>
								)}
							</Box>
						</Paper>
					))}

					<Divider sx={{my: 3}} />

					<Box display="flex" flexDirection="column" gap={2} mb={3}>
						<FormControlLabel
							sx={{alignItems: "flex-start", mx: 0, gap: 1.5}}
							control={
								<Checkbox
									checked={expediteProcesses}
									onChange={(e) => setExpediteProcesses(e.target.checked)}
									sx={{
										mt: -0.5,
										color: colors.gray["300"],
										"&.Mui-checked": {color: colors.warmBlue["900"]},
									}}
								/>
							}
							label={
								<Box>
									<Text.Small.SemiBold color={colors.gray["800"]}>
										Expedite processes
									</Text.Small.SemiBold>
									<Text.Small.Regular color={colors.gray["500"]}>
										Skips NOA wait and email/phone scrubs — schedules comms immediately
									</Text.Small.Regular>
								</Box>
							}
						/>
						<FormControlLabel
							sx={{alignItems: "flex-start", mx: 0, gap: 1.5}}
							control={
								<Checkbox
									checked={simulateScrubs}
									onChange={(e) => setSimulateScrubs(e.target.checked)}
									sx={{
										mt: -0.5,
										color: colors.gray["300"],
										"&.Mui-checked": {color: colors.warmBlue["900"]},
									}}
								/>
							}
							label={
								<Box>
									<Text.Small.SemiBold color={colors.gray["800"]}>
										Simulate scrubs
									</Text.Small.SemiBold>
									<Text.Small.Regular color={colors.gray["500"]}>
										Writes &quot;not found&quot; results for bankruptcy, deceased, and service
										members scrubs
									</Text.Small.Regular>
								</Box>
							}
						/>
					</Box>

					<Button
						variant="contained"
						fullWidth
						disabled={submitting || !effectiveClientID}
						onClick={() => createCustomer()}
						startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
						sx={[
							Text.Small.Bold.css,
							{
								background: colors.warmBlue["900"],
								borderRadius: "8px",
								py: 1.75,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								boxShadow: "none",
								"&:hover": {
									background: colors.warmBlue["800"],
									boxShadow: "0px 4px 14px 2px rgba(54,59,100,0.10)",
								},
								"&:active": {background: colors.warmBlue["700"]},
							},
						]}
					>
						{submitting ? "Uploading..." : "Create Customer & Accounts"}
					</Button>
				</Box>
			</Box>
		</Layout>
	)
}

export {CreateCustomerPage}
